import { useState } from 'react';
import { Card } from '@/components/ui/Card';
import { FeaturedEventCard } from '@/components/common/FeaturedEventCard';
import { useFeaturedEvent, useRegisterForEvent, useRegisteredEvents } from '@/hooks/useEvents';
import { toFeaturedEventProps } from '@/features/events/event.mappers';
import { FROM_DASHBOARD } from '@/features/events/event-detail-back';
import { MemberRegistrationConfirmedDialog } from '@/features/events/components/MemberRegistrationConfirmedDialog';
import { eventDetailPath } from '@/lib/constants/routes';
import { DASHBOARD_STRINGS } from '@/lib/constants/dashboard-strings';

/**
 * The live featured event on the member dashboard (UX sheet, StartupAdminVF
 * r16-r18). One event, so a singular heading with no count and no "View All".
 * Renders nothing while loading, on error, or when nothing is featured: it is
 * optional page furniture, the same rule as the events page's banner.
 * No access-type tag: members do not need Public / Members Only here (UX sheet
 * v2). The backend's date label already includes the end time.
 */
export function DashboardFeaturedEvent() {
  const { data, isPending, isError } = useFeaturedEvent();
  // The featured endpoint is public and has no user context, so registration
  // is derived from the (cached) registered-events list, as the events page does.
  const { data: registered } = useRegisteredEvents();

  // Hook runs before the early return; the id is only used once an event exists.
  const { mutate: register, isPending: isRegistering } = useRegisterForEvent(data?.id ?? '');
  const [registeredOpen, setRegisteredOpen] = useState(false);

  if (isPending || isError || !data) return null;

  return (
    <Card shadow="soft" hoverEffect={false} className="w-full">
      <section aria-labelledby="dashboard-featured-heading" className="flex flex-col gap-6">
        <h2 id="dashboard-featured-heading" className="text-h3 text-teal">
          {DASHBOARD_STRINGS.FEATURED_TITLE}
        </h2>
        <FeaturedEventCard
          event={toFeaturedEventProps(data)}
          isRegistered={(registered ?? []).some((event) => event.id === data.id)}
          detailPath={eventDetailPath(data.id)}
          detailState={FROM_DASHBOARD}
          onRegister={() => register(undefined, { onSuccess: () => setRegisteredOpen(true) })}
          isRegistering={isRegistering}
        />
        <MemberRegistrationConfirmedDialog
          eventId={data.id}
          open={registeredOpen}
          onClose={() => setRegisteredOpen(false)}
        />
      </section>
    </Card>
  );
}
